/**
 * Vanilla tRPC client for the small set of PUBLIC, session-cookie-issuing
 * procedures the EMPLOYEE ESS invite-accept flow calls BEFORE any OAuth
 * token exists — `essInviteSignup.{lookup, acceptWithCredentials}`
 * (`apps/api/src/trpc/routers/ess_invite_signup.ts`), used ONLY by
 * `invite_accept_screen.tsx`.
 *
 * `wagepro_signup_screen.tsx`/`wagepro_login_screen.tsx` used to also call
 * this client (for `wageproSignup.provision`/`auth.login`, followed by a
 * `beginSignIn()` OAuth handoff) — as of the WagePro standalone-admin-auth
 * pivot off OAuth, both screens call `wageproAuth.signup`/`wageproAuth.login`
 * through `lib/trpc.ts`'s now-cookie-based `getTrpcClient()` directly
 * instead, since that client's transport is now identical to this one
 * (`credentials: 'include'`) — a separate client is no longer needed for
 * the admin path. This module survives ONLY because the employee ESS
 * invite-accept flow (`invite_accept_screen.tsx`) is a DIFFERENT plane
 * (Personal-Workspace, ADR-030) that follows up its own cookie-issuing call
 * with `beginSignIn()` (`lib/auth/oauth_pkce.ts`) — a genuinely separate
 * concern from the admin console's auth, kept out of scope for this pass.
 *
 * `essInviteSignup.acceptWithCredentials`
 * (`apps/api/src/trpc/routers/ess_invite_signup.ts`) appends a `Set-Cookie`
 * for the unified `mukil_session` — the SAME cookie
 * `firmInviteAcceptProcedure`/`auth.login` issue — on success.
 * `invite_accept_screen.tsx` follows up with `beginSignIn()`
 * (`lib/auth/oauth_pkce.ts`), a full-page top-level navigation to
 * `GET /oauth/authorize` on the SAME api origin.
 *
 * `credentials: 'include'` here is what makes the browser actually STORE
 * that `Set-Cookie` response header for the API's origin in the first
 * place — the default `fetch` credentials mode ('same-origin') would
 * silently drop it, since wagepro-web (`:3013` in dev) and `apps/api`
 * (`:8788` in dev) are different origins. The cookie's own `SameSite=Lax`
 * attribute (`session_cookie.ts`) then governs whether it is SENT on the
 * later `/oauth/authorize` request: `Lax` withholds it from cross-site
 * *sub-resource* requests (this very fetch, on the way back) but DOES send
 * it on a cross-site top-level GET navigation — exactly what
 * `beginSignIn()`'s `window.location.href = authorizeUrl` performs. `Lax`
 * also does not require `Secure`, so this works over plain `http://` in
 * local dev (`session_cookie.ts`'s dev branch already omits `Secure`).
 *
 * `localhost:3013` and `localhost:8788` are cross-ORIGIN (different ports)
 * but same-SITE (same scheme + host, `SameSite` is a site-level, not
 * origin-level, concept) — so this pairing behaves in local dev exactly as
 * it will in production (`app.wagepro.ai` calling `api.ledgerpro.ai`, a
 * genuinely different eTLD+1 — see `apps/api/.dev.vars.example`'s
 * `WAGEPRO_OAUTH_AUDIENCE`/`WAGEPRO_APP_BASE_URL` for how those origins are
 * configured).
 */
import { createTRPCProxyClient, httpLink } from '@trpc/client';
import type { AppRouter } from 'ledger-api/trpc';
import { resolveApiUrl } from '../trpc.js';

export function createPublicAccountTrpcClient() {
  return createTRPCProxyClient<AppRouter>({
    links: [
      httpLink({
        url: `${resolveApiUrl()}/trpc`,
        fetch(url, options) {
          return fetch(url, { ...options, credentials: 'include' });
        },
      }),
    ],
  });
}

let _client: ReturnType<typeof createPublicAccountTrpcClient> | null = null;

export function getPublicAccountTrpcClient() {
  if (!_client) _client = createPublicAccountTrpcClient();
  return _client;
}

export function resetPublicAccountTrpcClient() {
  _client = null;
}
